import React from "react";
import { FaCheck } from "react-icons/fa";
import {
  catalogAgreesWithEntered,
  uniqueAutoCatalogMatch,
} from "../../../utility/catalogAverageMatch";

const averageLabel = (match) => {
  if (match.average == null) {
    return "No Average";
  }
  return `${match.average}${
    match.games != null ? ` (${match.games} games)` : ""
  }`;
};

const selectedMessage = (selected, catalogName, enteredAverage) => {
  const fromCatalog = catalogName ? ` in ${catalogName}` : "";
  if (selected.average == null) {
    return `Catalog has no average with enough games${fromCatalog}. Marked as No Average.`;
  }
  const agrees = catalogAgreesWithEntered(selected, {
    average: enteredAverage,
    hasAverage: enteredAverage != null && enteredAverage !== "",
  });
  if (agrees) {
    return `This average matches the catalog${fromCatalog}. Verify it on Averages.`;
  }
  if (enteredAverage != null && enteredAverage !== "") {
    return `Using ${enteredAverage}. Catalog has ${averageLabel(selected)}${fromCatalog}. Verify it on Averages.`;
  }
  return `Average is from ${selected.source}${fromCatalog}. Verify it on Averages.`;
};

export const CatalogMatchList = ({
  catalogName,
  suggestions = [],
  selected = null,
  enteredAverage = null,
  hasPreviousAverage = false,
  onSelect,
  onClear,
}) => {
  if (selected) {
    return (
      <div className="text-xs text-brand-muted flex items-start gap-2">
        <FaCheck className="text-brand-muted mt-0.5 flex-shrink-0" />
        <div>
          <p>{selectedMessage(selected, catalogName, enteredAverage)}</p>
          {onClear ? (
            <button
              type="button"
              onClick={onClear}
              className="mt-1 text-brand-gold-ink hover:underline"
            >
              Use previous average
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  if (!suggestions.length) return null;

  const unique = uniqueAutoCatalogMatch(suggestions);
  const bowlerForCompare = {
    average: enteredAverage,
    hasAverage: enteredAverage != null,
  };
  const agrees =
    hasPreviousAverage &&
    unique &&
    catalogAgreesWithEntered(unique, bowlerForCompare);

  if (agrees) {
    return null;
  }

  const rows = unique ? [unique] : suggestions;
  const uniqueDisagrees =
    hasPreviousAverage &&
    unique &&
    !catalogAgreesWithEntered(unique, bowlerForCompare);
  const previousLabel =
    enteredAverage == null ? "No Average" : String(enteredAverage);

  if (uniqueDisagrees) {
    return (
      <p className="text-xs text-brand-muted">
        Using {previousLabel}. Catalog has {averageLabel(unique)}
        {catalogName ? ` in ${catalogName}` : ""}. Verify it on Averages.
      </p>
    );
  }

  return (
    <div className="space-y-1">
      {rows.length > 1 ? (
        <p className="text-xs font-medium text-brand-charcoal">Catalog</p>
      ) : null}
      <div className="border border-brand-charcoal/15 rounded-lg divide-y divide-brand-charcoal/10 overflow-hidden">
        {rows.map((match) => (
          <button
            key={match.extractedBowlerID}
            type="button"
            onClick={() => onSelect(match)}
            className="w-full px-3 py-2 text-left hover:bg-brand-cream focus:bg-brand-cream focus:outline-none"
          >
            <div className="font-medium text-sm text-brand-charcoal">
              Use catalog average
            </div>
            <div className="text-xs text-brand-muted">
              {match.name}
              {" · "}
              {averageLabel(match)}
              {match.source ? ` · ${match.source}` : ""}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};
